import { isVideoStation } from '@app/core/stations/helpers';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useStreamSettingsStore } from '@app/modules/logger/screens/stream/stores/stream-screen-settings.store';
import { useStreamSelection } from '@app/modules/logger/screens/stream/stores/useStreamSelection';
import { webApi } from '@mi-front/api-clients';
import { toDateTimeStr } from '@mi-front/core';
import { useCallback } from 'react';
import { useDownloadMedia } from './useDownloadMedia';

export interface UseDownloadMediaFromSelectionReturn {
  /** Starts download for the current selection; no-op if there is no selection. */
  downloadMedia: () => void;
  isPreparingDownload: boolean;
}

/**
 * Adapter that wires the DownloadMedia feature to stream selection.
 * Used by the selection menu and hotkeys: resolves station and selection range, then calls the core startDownload.
 * Syncs isPreparingDownload to the stream settings store so the menu loading state stays in sync when triggered via hotkey.
 */
export function useDownloadMediaFromSelection(): UseDownloadMediaFromSelectionReturn {
  const station = useActiveStation();
  const { selectionRange, selectionExists } = useStreamSelection();
  const isPreparingDownload = useStreamSettingsStore((s) => s.isPreparingSelectionDownload);
  const setPreparingSelectionDownload = useStreamSettingsStore((s) => s.setPreparingSelectionDownload);
  const { startDownload } = useDownloadMedia({
    onPreparingChange: setPreparingSelectionDownload,
  });

  const downloadMedia = useCallback(() => {
    if (!selectionExists) {
      return;
    }

    const { from, to } = selectionRange;
    const type = isVideoStation(station)
      ? webApi.MediaExportJobEnqueueDtoTypeEnum.VIDEO
      : webApi.MediaExportJobEnqueueDtoTypeEnum.AUDIO;

    startDownload({
      stationId: station.id,
      type,
      from: toDateTimeStr(from),
      to: toDateTimeStr(to),
    });
  }, [selectionExists, selectionRange, station, startDownload]);

  return { downloadMedia, isPreparingDownload };
}
